import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { memo, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { BarChartLegend } from "./bar-chart-legend";
import { getBarChartOptions } from "./chart-options";
import * as redoBarChartCss from "./redo-bar-chart.module.css";
import { stackedBarTopRadiusPlugin } from "./stacked-bar-top-radius-plugin";
import { RedoBarChartProps } from "./types";
import { useBarChartData } from "./useBarChartData";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export const RedoBarChart = memo(function RedoBarChart({
  series,
  labels,
  leftAxisLabel,
  bottomAxisLabel,
  legendPosition,
  showLegend,
  valueAxisFormatter,
  titleText,
  stacked = false,
  horizontal = false,
  tooltipFormat,
  onBarClick,
  valueAxisMax,
  roundBothEnds = false,
  emphasizeZeroLine = false,
  maxBarThickness = 32,
}: RedoBarChartProps) {
  const processedData = useBarChartData(
    series,
    labels,
    stacked,
    horizontal,
    maxBarThickness,
  );
  const shouldShowLegend =
    showLegend ?? processedData.normalizedSeries.length > 1;

  // Track which datasets are visible (all visible by default)
  const [visibleDatasets, setVisibleDatasets] = useState<Set<number>>(
    () => new Set(processedData.normalizedSeries.map((_, index) => index)),
  );

  // Toggle dataset visibility
  const handleLegendClick = (index: number) => {
    setVisibleDatasets((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Filter chart data based on visibility
  const filteredChartData = useMemo(() => {
    return {
      ...processedData,
      datasets: processedData.datasets.map((dataset, index) => ({
        ...dataset,
        hidden: !visibleDatasets.has(index),
      })),
    };
  }, [processedData, visibleDatasets]);

  // Memoize plugins array to prevent re-creation on every render
  const plugins = useMemo(
    () => (stacked && !horizontal ? [stackedBarTopRadiusPlugin] : []),
    [stacked, horizontal],
  );

  const baseOptions = getBarChartOptions({
    leftAxisLabel,
    bottomAxisLabel,
    valueAxisFormatter,
    series: processedData.normalizedSeries,
    stacked,
    horizontal,
    tooltipFormat,
    valueAxisMax,
    emphasizeZeroLine,
  });

  const options = {
    ...baseOptions,
    maintainAspectRatio: false,
    responsive: true,
    plugins: { ...baseOptions.plugins, stackedBarTopRadius: { roundBothEnds } },
    onClick: onBarClick
      ? (event: any, elements: any[]) => {
          if (elements.length > 0) {
            const element = elements[0];
            const datasetIndex = element.datasetIndex;
            const dataIndex = element.index;
            const label = processedData.labels[dataIndex];
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            const value = processedData.datasets[datasetIndex].data[dataIndex];
            if (value !== null) {
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              onBarClick(label, value, datasetIndex);
            }
          }
        }
      : undefined,
    onHover: onBarClick
      ? (event: any, elements: any[]) => {
          const target = event.native?.target as HTMLElement | undefined;
          if (target) {
            target.style.cursor = elements.length > 0 ? "pointer" : "default";
          }
        }
      : undefined,
  };

  return (
    <Flex data-arbiter="RedoBarChart" dir="column" height="full" w="full">
      {titleText && (
        <Flex align="center" justify="space-between" mb="md" w="full">
          <Text fontSize="sm" fontWeight="medium" textColor="tertiary">
            {titleText}
          </Text>
        </Flex>
      )}

      {shouldShowLegend && legendPosition === "top" && (
        <Flex justify="flex-end" pr="md" w="full">
          <BarChartLegend
            onItemClick={handleLegendClick}
            position={legendPosition}
            series={processedData.normalizedSeries}
            visibleDatasets={visibleDatasets}
          />
        </Flex>
      )}

      <Flex grow={1} height="full" w="full">
        <Flex
          className={redoBarChartCss.chartContainer}
          height="full"
          minW="none"
          position="relative"
          w="full"
        >
          <Bar
            data={filteredChartData as ChartData<"bar">}
            options={options}
            plugins={plugins}
          />
        </Flex>

        {shouldShowLegend && legendPosition === "right" && (
          <BarChartLegend
            onItemClick={handleLegendClick}
            position={legendPosition}
            series={processedData.normalizedSeries}
            visibleDatasets={visibleDatasets}
          />
        )}
      </Flex>

      {shouldShowLegend && legendPosition === "bottom" && (
        <BarChartLegend
          onItemClick={handleLegendClick}
          position={legendPosition}
          series={processedData.normalizedSeries}
          visibleDatasets={visibleDatasets}
        />
      )}
    </Flex>
  );
});
